import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { firstValueFrom, Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { Utils } from "~app/common/services/utils";
import { DateUtils } from "~app/common/utils/date-utils";
import { VKKontraktUtskriftDTO } from "~app/features/virkeskop/models/utskrift/utskrift-models";
import { KontraktDokumentService } from "~app/features/virkeskop/services/kontrakt-dokument.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { DigitalSigneringFormGroup, UtskriftFormGroup } from "../../digital-signernig-form";

@Component({
    selector: "rask-kontrakt-signering-utskrift",
    imports: [RaskCommonModule],
    templateUrl: "./kontrakt-signering-utskrift.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./kontrakt-signering-utskrift.component.scss"
})
export class KontraktSigneringUtskriftComponent implements OnInit {
    protected utskriftFormGroup: UtskriftFormGroup;
    protected kontraktId$: Observable<number>;
  @Input() protected readonly utskrift: VKKontraktUtskriftDTO;
  @Input() protected dokumentTyp: string;
  @Input() private readonly form: DigitalSigneringFormGroup;
  private readonly fb = inject(FormBuilder);
  private readonly kontraktDokumentService = inject(KontraktDokumentService);
  private readonly kontraktMediatorService = inject(KontraktMediatorService);

  constructor() {
      this.kontraktId$ = this.kontraktMediatorService.kontrakt$.pipe(
          map(kontrakt => kontrakt.id),
          shareReplay(1)
      );
  }

  ngOnInit(): void {
      this.initForm();
  }

  getDate(date: string): string {
      return DateUtils.formatDateString(date, DateUtils.dateformat);
  }
  itemCanBeDeSelected(): boolean {
      return this.utskrift.typ === "Kontrakt-karta" || this.utskrift.typ === "Kontrakt-bilaga" || this.utskrift.typ === "Kontrakt-skogsvardsKarta";
  }

  isKontrakt(): boolean {
      return this.utskrift.typ === "Kontrakt" || this.utskrift.typ === "SkogsvardsKontrakt";
  }

  protected get utskrifterFormArray() {
      return this.form.controls.utskrifter;
  }

  protected async openKontrakt(dokumentId: number) {
      const kontraktId = await firstValueFrom(this.kontraktId$);
      this.kontraktDokumentService
          .getDokumentForKontraktDownload(kontraktId, dokumentId)
          .subscribe((blob: Blob) => {
              const url = URL.createObjectURL(blob);
              window.open(url);
          });
      return false;
  }

  protected async downloadDokument(kontrakt: VKKontraktUtskriftDTO) {
      const kontraktId = await firstValueFrom(this.kontraktId$);
      this.kontraktDokumentService.getDokumentForKontraktDownload(kontraktId, kontrakt.id).subscribe((blob: Blob) => {
          Utils.downloadBlob(blob, kontrakt.dokumentNamn);
      });
      return false;
  }

  private initForm(): void {
      this.utskriftFormGroup = this.utskrifterFormArray.controls.find(c => c.controls.id.value === this.utskrift.id);

      if (!this.utskriftFormGroup) {
          this.utskriftFormGroup = this.fb.group({
              id: [this.utskrift.id],
              selected: [true]
          });

          this.utskrifterFormArray.push(this.utskriftFormGroup);
          if (this.utskrift.typ === "Kontrakt-bilaga") {
              this.utskriftFormGroup.disable();
              this.utskriftFormGroup.controls.selected.setValue(false);
          }
          this.initBilagaControl();
      }
  }

  private initBilagaControl(): void {
      if (this.dokumentTyp === "Kontrakt" && this.utskrift.typ === "Kontrakt-skogsvardsKarta") {
          this.utskriftFormGroup.controls.selected.setValue(false);
      }
      if (!this.itemCanBeDeSelected()) {
          this.utskriftFormGroup.controls.selected.disable();
      }
  }
}
